import React from "react";
import { graphql, useFragment } from "react-relay";

import { faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import {
	MenuItemToBeDisabledForExternalWorkspaces,
	MenuItemTooltip,
	useGetPropsForExternalWorkspaces,
} from "~/meetings/MeetingHeader/MoreActionsMenu/MenuItemTooltip";
import DeleteNoteModal, { type DeletionType } from "~/stream/DeleteNoteModal/DeleteNoteModal";
import { useModals } from "~/ui";
import toast from "~/ui/toast";

import type { DeleteMeeting_note$key } from "~/__generated__/DeleteMeeting_note.graphql";
import type { DeleteMeeting_viewer$key } from "~/__generated__/DeleteMeeting_viewer.graphql";

const viewerFragment = graphql`
	fragment DeleteMeeting_viewer on Viewer {
		user {
			isGuest
		}
	}
`;

const noteFragment = graphql`
	fragment DeleteMeeting_note on Note {
		id
		viewerCanEdit
		isLocked
		...MenuItemTooltip_useGetPropsForExternalWorkspaces_note
		...DeleteNoteModal_note
	}
`;

export default function DeleteMeetingMenuItem({
	note: noteKey,
	viewer: viewerKey,
	selectedRecordingId,
	onDeleteExecuted,
}: {
	note: DeleteMeeting_note$key;
	viewer: DeleteMeeting_viewer$key;
	selectedRecordingId?: string;
	onDeleteExecuted: (type: DeletionType) => void;
}) {
	const modals = useModals();
	const note = useFragment(noteFragment, noteKey);
	const viewer = useFragment(viewerFragment, viewerKey);
	const tooltipPropsForExternalWorkspaces = useGetPropsForExternalWorkspaces(note);

	const onSelectDelete = React.useCallback(async () => {
		if (viewer.user.isGuest) return;

		// Not sure if we can run into the case where a user doesn't have delete permissions for some recordings,
		// but need to check this permission somehow
		if (!note.viewerCanEdit || note.isLocked) {
			toast.error("You don't have permission to delete all recaps for this note.");
			return;
		}

		const result = await modals.open<DeletionType>(({ close }) => (
			<DeleteNoteModal onClose={close} note={note} recordingId={selectedRecordingId} />
		));

		if (result) onDeleteExecuted(result);
	}, [modals, note, viewer.user.isGuest, selectedRecordingId, onDeleteExecuted]);

	return (
		<MenuItemToBeDisabledForExternalWorkspaces
			icon={faTrashCan}
			onSelect={onSelectDelete}
			disabled={note.isLocked || !note.viewerCanEdit}
			destructive
			{...tooltipPropsForExternalWorkspaces}
		>
			<MenuItemTooltip
				disabled={!(note.isLocked || !note.viewerCanEdit)}
				content={
					note.isLocked
						? "Cannot delete locked notes."
						: !note.viewerCanEdit
							? "You have limited access to this meeting"
							: null
				}
			>
				<div>Delete…</div>
			</MenuItemTooltip>
		</MenuItemToBeDisabledForExternalWorkspaces>
	);
}
